// Board Pack Generator — one-click 20-slide portfolio PPT for Platinum.
//
// Runs 100% client-side using pptxgenjs (already in package.json). Fetches
// live data from /api/csuite/rollup + /api/platinum/{scorecard,value,insights,
// hana,dynamics365} and lays out a magazine-grade deck that mirrors the
// dark-cherry theme of the Executive Portal.

import PptxGenJS from "pptxgenjs";
import { csuiteApi } from "./csuiteApi";
import { api } from "./api";
import { applyCustomPptxTemplate } from "./templateSwap";

const CHERRY       = "7A1F2B";
const CHERRY_DEEP  = "4A0F1A";
const CHERRY_SOFT  = "B03A48";
const INK          = "2A0A10";
const INK_SOFT     = "5C1F2A";
const PAPER        = "FDFBFB";
const SURFACE      = "FDF2F4";
const BORDER       = "F0D3D8";
const RAG          = { Green: "16A34A", Amber: "D97706", Red: "DC2626" };
const HEALTH_HEX   = { "On Track": "16A34A", "At Risk": "D97706", "Off Track": "DC2626", "On Hold": "64748B" };
const money = (n) => n >= 1_000_000 ? `$${(n / 1_000_000).toFixed(1)}M` : n >= 1_000 ? `$${(n / 1_000).toFixed(0)}K` : `$${n}`;
const todayStr = () => new Date().toISOString().slice(0, 10);

export async function generateBoardPack({ orgName = "Enterprise Portfolio", useTemplate = false } = {}) {
  // Pull every data source in parallel. Any failure falls back to null and
  // the corresponding slide reads "Data pending".
  const [rollup, scorecard, value, insights, hana, d365, schAtRisk] = await Promise.all([
    csuiteApi.rollup().catch(() => null),
    csuiteApi.scorecard().catch(() => null),
    csuiteApi.value().catch(() => null),
    csuiteApi.insights().catch(() => null),
    csuiteApi.hana().catch(() => null),
    csuiteApi.dynamics365().catch(() => null),
    api.scheduleAtRisk().catch(() => null),
  ]);

  const ppt = new PptxGenJS();
  ppt.layout = "LAYOUT_WIDE"; // 13.333 × 7.5
  const W = 13.333, H = 7.5;

  // ── Slide 1 · Magazine cover ──────────────────────────────────────────
  const cover = ppt.addSlide();
  cover.background = { color: CHERRY };
  cover.addShape(ppt.ShapeType.rect, { x: W - 0.15, y: 0, w: 0.15, h: H, fill: { color: CHERRY_SOFT }, line: { color: CHERRY_SOFT } });
  cover.addText("CAPITTANCE  ·  BOARD PACK", { x: 0.6, y: 0.5, w: 12, h: 0.35, fontSize: 12, bold: true, color: "FFFFFF", charSpacing: 4 });
  cover.addText("EXECUTIVE PORTFOLIO REVIEW · CONFIDENTIAL", { x: 0.6, y: 0.82, w: 12, h: 0.3, fontSize: 9, color: "E5A9B3", charSpacing: 2 });
  cover.addText(orgName, { x: 0.6, y: 3.0, w: 12, h: 2.2, fontSize: 60, bold: true, color: "FFFFFF" });
  cover.addText(`GENERATED  ${todayStr().toUpperCase()}`, { x: 0.6, y: 6.6, w: 12, h: 0.4, fontSize: 12, bold: true, color: "E5A9B3", charSpacing: 3 });

  // ── Slide 2 · Contents ────────────────────────────────────────────────
  const toc = ppt.addSlide();
  toc.background = { color: PAPER };
  drawHeader(toc, ppt, "Contents", "20 slides", W);
  const tocRows = [
    ["01", "Cover"], ["02", "Contents"], ["03", "Executive Summary"], ["04", "Portfolio KPIs"],
    ["05", "Portfolio Health"], ["06", "Business-Unit Scorecard"], ["07", "Risk Heatmap"],
    ["08", "SAP S/4HANA Cockpit"], ["09", "Dynamics 365 · Finance"], ["10", "Dynamics 365 · Sales"],
    ["11", "Supply Chain"], ["12", "Customer Service"], ["13", "Value Realization"],
    ["14", "AI Insights · Highlights"], ["15", "Milestones"], ["16", "Budget"],
    ["17", "Open Risks"], ["18", "Open Actions"], ["19", "Decisions Requested"], ["20", "Next Quarter Focus"],
  ];
  tocRows.forEach((r, i) => {
    const col = i % 2, row = Math.floor(i / 2);
    const x = 0.6 + col * 6.2, y = 1.5 + row * 0.5;
    toc.addText(r[0], { x, y, w: 0.7, h: 0.4, fontSize: 22, bold: true, color: CHERRY });
    toc.addText(r[1], { x: x + 0.8, y: y + 0.05, w: 5, h: 0.3, fontSize: 14, color: INK });
  });
  drawFooter(toc, "02 · Contents", W, H);

  // ── Slide 3 · Executive Summary ───────────────────────────────────────
  const es = ppt.addSlide();
  es.background = { color: PAPER };
  drawHeader(es, ppt, "Executive Summary", "This quarter at a glance", W);
  const proj = rollup?.projects || [];
  const cAll = rollup?.aggregates?.constraintCounts || [];
  const openRisks = cAll.reduce((s, c) => s + (c.risksOpen || 0), 0);
  const openIssues = cAll.reduce((s, c) => s + (c.issuesOpen || 0), 0);
  const summaryText = [
    { text: "Portfolio scale · ", options: { bold: true, color: CHERRY_DEEP } },
    { text: `${proj.length} projects, avg progress ${Math.round(proj.reduce((s, p) => s + (p.progress || 0), 0) / (proj.length || 1))}%, ${openRisks} open risks, ${openIssues} active issues.\n\n` },
    { text: "Health mix · ", options: { bold: true, color: CHERRY_DEEP } },
    { text: `${countBy(proj, "health", "On Track")} on track  ·  ${countBy(proj, "health", "At Risk")} at risk  ·  ${countBy(proj, "health", "Off Track")} off track.\n\n` },
    { text: "Financials · ", options: { bold: true, color: CHERRY_DEEP } },
    { text: `Portfolio budget ${money(sumBy(proj, "budget"))}  ·  Value realised ${value ? money(value.totals.achieved) : "—"} of ${value ? money(value.totals.planned) : "—"} planned  ·  ${value ? Math.round(value.totals.achieved / value.totals.planned * 100) : 0}% realisation.\n\n` },
    { text: "S/4HANA · ", options: { bold: true, color: CHERRY_DEEP } },
    { text: `${hana ? hana.migration.waves.filter(w => w.status === "Live").length : 0} waves live, next cutover ${hana?.migration.waves.find(w => w.status === "In Cutover")?.date || "—"}.\n\n` },
    { text: "Dynamics 365 · ", options: { bold: true, color: CHERRY_DEEP } },
    { text: `Revenue YTD ${d365 ? money(d365.finance.revenueYtd) : "—"}  ·  EBITDA ${d365 ? d365.finance.ebitdaPct + "%" : "—"}  ·  Pipeline ${d365 ? money(d365.sales.pipelineTotal) : "—"}.` },
  ];
  es.addText(summaryText, { x: 0.6, y: 1.5, w: 12.1, h: 5, fontSize: 13, color: INK, valign: "top", paraSpaceAfter: 6 });
  drawFooter(es, "03 · Executive Summary", W, H);

  // ── Slide 4 · Portfolio KPIs (6 tiles) ────────────────────────────────
  const kpi = ppt.addSlide();
  kpi.background = { color: PAPER };
  drawHeader(kpi, ppt, "Portfolio KPIs", `As of ${todayStr()}`, W);
  const kTiles = [
    { label: "PROJECTS",         value: proj.length,                                                  sub: `Across ${new Set(proj.map(p => p.phase)).size} phases`, color: CHERRY },
    { label: "AVG PROGRESS",     value: `${Math.round(proj.reduce((s, p) => s + (p.progress || 0), 0) / (proj.length || 1))}%`, sub: `${countBy(proj, "health", "On Track")} on track`, color: "16A34A" },
    { label: "OPEN RISKS",       value: String(openRisks),                                            sub: `${countBy(proj, "health", "At Risk")} at-risk projects`, color: "DC2626" },
    { label: "OPEN ISSUES",      value: String(openIssues),                                           sub: "Cross-portfolio",   color: "D97706" },
    { label: "PORTFOLIO BUDGET", value: money(sumBy(proj, "budget")),                                 sub: "Approved plan",     color: CHERRY_DEEP },
    { label: "VALUE REALISED",   value: value ? `${Math.round(value.totals.achieved / value.totals.planned * 100)}%` : "—",  sub: value ? `${money(value.totals.achieved)} of ${money(value.totals.planned)}` : "Data pending", color: "0369A1" },
  ];
  drawKpiGrid(kpi, ppt, kTiles);
  drawFooter(kpi, "04 · Portfolio KPIs", W, H);

  // ── Slide 5 · Portfolio Health table ──────────────────────────────────
  const ph = ppt.addSlide();
  ph.background = { color: PAPER };
  drawHeader(ph, ppt, "Portfolio Health", `${proj.length} project${proj.length === 1 ? "" : "s"}`, W);
  const phRows = [
    [head("Project"), head("Phase"), head("Health"), head("Progress"), head("PM"), head("Budget")],
    ...proj.slice(0, 12).map(p => [
      cell(p.name),
      cell(p.phase || "—"),
      { text: p.health || "—", options: { bold: true, color: HEALTH_HEX[p.health] || INK_SOFT, align: "center" } },
      { text: `${p.progress || 0}%`, options: { align: "center", color: INK_SOFT } },
      cell(p.pm || "—"),
      { text: money(p.budget || 0), options: { align: "right", color: INK, bold: true } },
    ]),
  ];
  ph.addTable(phRows, { x: 0.5, y: 1.4, w: 12.3, fontSize: 11, border: { type: "solid", pt: 0.5, color: BORDER }, colW: [3.5, 1.8, 1.4, 1.4, 2.2, 2] });
  drawFooter(ph, "05 · Portfolio Health", W, H);

  // ── Slide 6 · Business-Unit Scorecard ─────────────────────────────────
  const sc = ppt.addSlide();
  sc.background = { color: PAPER };
  drawHeader(sc, ppt, "Business-Unit Scorecard", "RAG · 5 dimensions · weighted score", W);
  const scRows = [
    [head("Business Unit"), head("Revenue"), head("Margin"), head("Delivery"), head("People"), head("Risk"), head("Score")],
    ...(scorecard?.units || []).map(u => [
      cell(u.bu),
      ...["revenue","margin","delivery","people","risk"].map(d => ({ text: "●", options: { align: "center", color: RAG[u[d]] || "71717A", bold: true, fontSize: 22 } })),
      { text: String(u.score), options: { align: "right", bold: true, color: u.score >= 90 ? "16A34A" : u.score >= 75 ? "0369A1" : "D97706" } },
    ]),
  ];
  sc.addTable(scRows, { x: 0.5, y: 1.4, w: 12.3, fontSize: 11, border: { type: "solid", pt: 0.5, color: BORDER }, colW: [3.5, 1.4, 1.4, 1.4, 1.4, 1.4, 1.8] });
  drawFooter(sc, "06 · Business-Unit Scorecard", W, H);

  // ── Slide 7 · Risk Heatmap (12 top risks by criticality) ──────────────
  const rh = ppt.addSlide();
  rh.background = { color: PAPER };
  drawHeader(rh, ppt, "Risk Heatmap", "Cross-portfolio", W);
  const allRisks = (rollup?.aggregates?.criticalRisks || []).slice(0, 10);
  const risks = allRisks.length ? allRisks : [{ title: "Data pending — no critical risks reported", project: "", score: "—" }];
  risks.forEach((r, i) => {
    const y = 1.5 + i * 0.5;
    rh.addShape(ppt.ShapeType.roundRect, { x: 0.6, y, w: 0.8, h: 0.35, fill: { color: r.score === "Critical" ? "DC2626" : "D97706" }, line: { color: "FFFFFF" }, rectRadius: 0.04 });
    rh.addText(r.score || "—", { x: 0.6, y, w: 0.8, h: 0.35, fontSize: 9, bold: true, color: "FFFFFF", align: "center" });
    rh.addText(r.title || r.ref || "—", { x: 1.6, y, w: 8, h: 0.35, fontSize: 12, color: INK });
    rh.addText(r.project || "", { x: 9.8, y, w: 3, h: 0.35, fontSize: 10, color: INK_SOFT, italic: true });
  });
  drawFooter(rh, "07 · Risk Heatmap", W, H);

  // ── Slide 8 · SAP S/4HANA Cockpit ─────────────────────────────────────
  const h8 = ppt.addSlide();
  h8.background = { color: PAPER };
  drawHeader(h8, ppt, "SAP S/4HANA Cockpit", "Modules · Adoption · Migration waves", W);
  if (hana) {
    const m = hana.modules;
    const hanaTiles = [
      { label: "CLOSE · DAYS",       value: String(m.finance.closeDays),         sub: "Finance",       color: CHERRY },
      { label: "OTD RATE %",          value: `${m.logistics.otdRate}%`,           sub: "Logistics",     color: "16A34A" },
      { label: "PROCUREMENT · D",    value: String(m.procurement.cycleTimeD),    sub: "Cycle time",    color: "0369A1" },
      { label: "HEADCOUNT",          value: m.hcm.headcount.toLocaleString(),   sub: `${m.hcm.attritionPct}% attrition`, color: CHERRY_DEEP },
    ];
    drawKpiGrid(h8, ppt, hanaTiles, { compact: true });
    // Adoption bars (5 rows)
    (hana.adoption || []).forEach((a, i) => {
      const y = 4.4 + i * 0.35;
      h8.addText(a.module, { x: 0.6, y, w: 3.5, h: 0.3, fontSize: 11, color: INK });
      h8.addShape(ppt.ShapeType.roundRect, { x: 4.2, y: y + 0.03, w: 7, h: 0.24, fill: { color: SURFACE }, line: { color: SURFACE }, rectRadius: 0.04 });
      h8.addShape(ppt.ShapeType.roundRect, { x: 4.2, y: y + 0.03, w: 7 * (a.adoptedPct / 100), h: 0.24, fill: { color: CHERRY }, line: { color: CHERRY }, rectRadius: 0.04 });
      h8.addText(`${a.adoptedPct}%`, { x: 11.3, y, w: 1.5, h: 0.3, fontSize: 11, bold: true, color: CHERRY_DEEP });
    });
  } else h8.addText("Data pending — connect your SAP tenant.", { x: 0.6, y: 3, w: 12, h: 0.5, fontSize: 14, color: INK_SOFT });
  drawFooter(h8, "08 · SAP S/4HANA", W, H);

  // ── Slide 9 · D365 Finance ────────────────────────────────────────────
  const df = ppt.addSlide();
  df.background = { color: PAPER };
  drawHeader(df, ppt, "Microsoft Dynamics 365 · Finance", "Revenue · EBITDA · Cash", W);
  if (d365) {
    const f = d365.finance;
    drawKpiGrid(df, ppt, [
      { label: "REVENUE · MTD",  value: money(f.revenueMtd), sub: "Month-to-date", color: CHERRY },
      { label: "REVENUE · YTD",  value: money(f.revenueYtd), sub: "Year-to-date",  color: CHERRY_DEEP },
      { label: "EBITDA %",       value: `${f.ebitdaPct}%`,   sub: "Margin",        color: "16A34A" },
      { label: "CASH ON HAND",   value: money(f.cashOnHand), sub: `AR days · ${f.arDays}`, color: "0369A1" },
    ], { compact: true });
    // Simple bar chart of trailing 12 months revenue
    df.addChart(ppt.ChartType.line, [
      { name: "Revenue", labels: f.series.map(s => s.month), values: f.series.map(s => s.revenue) },
      { name: "EBITDA",  labels: f.series.map(s => s.month), values: f.series.map(s => s.ebitda) },
    ], { x: 0.6, y: 4.2, w: 12.1, h: 2.8, chartColors: [CHERRY, "0369A1"], showLegend: true, legendPos: "b", showTitle: false, catAxisLabelFontSize: 9, valAxisLabelFontSize: 9 });
  } else df.addText("Data pending — connect your Dynamics 365 tenant.", { x: 0.6, y: 3, w: 12, h: 0.5, fontSize: 14, color: INK_SOFT });
  drawFooter(df, "09 · D365 · Finance", W, H);

  // ── Slide 10 · D365 Sales ─────────────────────────────────────────────
  const ds = ppt.addSlide();
  ds.background = { color: PAPER };
  drawHeader(ds, ppt, "Microsoft Dynamics 365 · Sales", "Pipeline · Win rate · Regions", W);
  if (d365) {
    const s = d365.sales;
    drawKpiGrid(ds, ppt, [
      { label: "PIPELINE",    value: money(s.pipelineTotal),  sub: "Total",             color: CHERRY },
      { label: "WIN RATE",    value: `${s.winRate}%`,          sub: "Q trailing",        color: "16A34A" },
      { label: "AVG DEAL",    value: money(s.avgDealSize),    sub: "Blended",           color: "0369A1" },
      { label: "CYCLE · D",   value: `${s.salesCycleDays}d`,   sub: "Prospect → Close", color: CHERRY_DEEP },
    ], { compact: true });
    ds.addChart(ppt.ChartType.bar, [
      { name: "Pipeline value", labels: s.byStage.map(x => x.stage), values: s.byStage.map(x => x.value) },
    ], { x: 0.6, y: 4.2, w: 12.1, h: 2.8, chartColors: [CHERRY], showLegend: false, catAxisLabelFontSize: 9, valAxisLabelFontSize: 9, barDir: "col" });
  }
  drawFooter(ds, "10 · D365 · Sales", W, H);

  // ── Slide 11 · Supply Chain ───────────────────────────────────────────
  const sch = ppt.addSlide();
  sch.background = { color: PAPER };
  drawHeader(sch, ppt, "Supply Chain", "OTIF · Turns · Backlog · Risks", W);
  if (d365) {
    const sc2 = d365.supplyChain;
    drawKpiGrid(sch, ppt, [
      { label: "OTIF %",        value: `${sc2.onTimeInFullPct}%`, sub: "On-time-in-full", color: "16A34A" },
      { label: "TURNS",         value: String(sc2.inventoryTurns), sub: "Inventory turns", color: "0369A1" },
      { label: "BACKLOG · D",   value: `${sc2.backlogDays}d`,      sub: "Days",             color: "D97706" },
      { label: "CARRIER %",     value: `${sc2.carrierPerf}%`,      sub: "Performance",      color: CHERRY },
    ], { compact: true });
    (sc2.topRisks || []).forEach((r, i) => {
      const y = 4.5 + i * 0.5;
      sch.addShape(ppt.ShapeType.roundRect, { x: 0.6, y, w: 1.2, h: 0.35, fill: { color: r.severity === "High" ? "DC2626" : "D97706" }, line: { color: "FFFFFF" }, rectRadius: 0.05 });
      sch.addText(r.severity, { x: 0.6, y, w: 1.2, h: 0.35, fontSize: 10, bold: true, color: "FFFFFF", align: "center" });
      sch.addText(r.item, { x: 2, y, w: 8, h: 0.35, fontSize: 12, color: INK });
      sch.addText(`ETA ${r.eta}`, { x: 10.5, y, w: 2.5, h: 0.35, fontSize: 10, color: INK_SOFT, align: "right" });
    });
  }
  drawFooter(sch, "11 · Supply Chain", W, H);

  // ── Slide 12 · Customer Service ───────────────────────────────────────
  const cs = ppt.addSlide();
  cs.background = { color: PAPER };
  drawHeader(cs, ppt, "Customer Service", "CSAT · Handle time · Cases", W);
  if (d365) {
    const cs2 = d365.customerService;
    drawKpiGrid(cs, ppt, [
      { label: "CSAT",         value: `${cs2.csatPct}%`, sub: "Target 88%",         color: "16A34A" },
      { label: "FCR",          value: `${cs2.firstContactResolvedPct}%`, sub: "First-contact resolved", color: "0369A1" },
      { label: "AHT",          value: `${Math.round(cs2.avgHandleTimeS / 60)}m`, sub: "Avg handle time", color: CHERRY },
      { label: "ESCALATED",    value: String(cs2.escalated), sub: `of ${cs2.openCases} open cases`, color: "D97706" },
    ], { compact: true });
    cs.addChart(ppt.ChartType.bar, [
      { name: "Cases", labels: cs2.byCategory.map(c => c.category), values: cs2.byCategory.map(c => c.count) },
      { name: "SLA %", labels: cs2.byCategory.map(c => c.category), values: cs2.byCategory.map(c => c.sla) },
    ], { x: 0.6, y: 4.2, w: 12.1, h: 2.8, chartColors: [CHERRY, "0369A1"], showLegend: true, legendPos: "b", barDir: "col" });
  }
  drawFooter(cs, "12 · Customer Service", W, H);

  // ── Slide 13 · Value Realization ──────────────────────────────────────
  const vr = ppt.addSlide();
  vr.background = { color: PAPER };
  drawHeader(vr, ppt, "Value Realization", "Planned vs Achieved benefits", W);
  if (value) {
    drawKpiGrid(vr, ppt, [
      { label: "PLANNED",   value: money(value.totals.planned),  sub: "Board-approved",   color: CHERRY_DEEP },
      { label: "ACHIEVED",  value: money(value.totals.achieved), sub: `${Math.round(value.totals.achieved / value.totals.planned * 100)}% realised`, color: "16A34A" },
      { label: "ON TRACK",  value: money(value.totals.onTrack),  sub: "In-flight",         color: "0369A1" },
      { label: "AT RISK",   value: money(value.totals.atRisk),   sub: "Requires attention", color: "D97706" },
    ], { compact: true });
    const dataP = value.benefits.map(b => ({ name: b.benefit.split(" ").slice(0, 3).join(" "), planned: b.planned, achieved: b.achieved }));
    vr.addChart(ppt.ChartType.bar, [
      { name: "Planned",  labels: dataP.map(d => d.name), values: dataP.map(d => d.planned) },
      { name: "Achieved", labels: dataP.map(d => d.name), values: dataP.map(d => d.achieved) },
    ], { x: 0.6, y: 4.2, w: 12.1, h: 2.8, chartColors: ["94A3B8", CHERRY], showLegend: true, legendPos: "b", barDir: "col" });
  }
  drawFooter(vr, "13 · Value Realization", W, H);

  // ── Slide 14 · AI Insights ────────────────────────────────────────────
  const ai = ppt.addSlide();
  ai.background = { color: PAPER };
  drawHeader(ai, ppt, "AI Insights", "Curated by Claude 4.5", W);
  (insights?.items || []).slice(0, 4).forEach((it, i) => {
    const col = i % 2, row = Math.floor(i / 2);
    const x = 0.6 + col * 6.2, y = 1.5 + row * 2.8;
    ai.addShape(ppt.ShapeType.roundRect, { x, y, w: 6, h: 2.6, fill: { color: "FFFFFF" }, line: { color: BORDER, width: 0.5 }, rectRadius: 0.08 });
    ai.addShape(ppt.ShapeType.roundRect, { x, y, w: 0.08, h: 2.6, fill: { color: CHERRY }, line: { color: CHERRY }, rectRadius: 0.03 });
    ai.addText(it.date, { x: x + 0.3, y: y + 0.1, w: 5.5, h: 0.3, fontSize: 9, color: INK_SOFT, charSpacing: 2 });
    ai.addText(it.title, { x: x + 0.3, y: y + 0.4, w: 5.5, h: 0.4, fontSize: 14, bold: true, color: INK });
    (it.bullets || []).forEach((b, j) => {
      ai.addText(`• ${b}`, { x: x + 0.3, y: y + 0.9 + j * 0.5, w: 5.5, h: 0.5, fontSize: 10, color: INK, valign: "top" });
    });
  });
  drawFooter(ai, "14 · AI Insights", W, H);

  // ── Slide 15 · Milestones ─────────────────────────────────────────────
  const ms = ppt.addSlide();
  ms.background = { color: PAPER };
  drawHeader(ms, ppt, "Milestones", "Next 90 days", W);
  const mss = (rollup?.aggregates?.milestones90 || []).slice(0, 8);
  const msRows = [
    [head("Project"), head("Milestone"), head("Date"), head("Status")],
    ...(mss.length ? mss : [{ project: "—", name: "No upcoming milestones", date: "—", status: "—" }]).map(m => [
      cell(m.project || "—"), cell(m.name), cell(m.date), { text: m.status || "—", options: { color: m.status === "Completed" ? "16A34A" : INK_SOFT, bold: true } },
    ]),
  ];
  ms.addTable(msRows, { x: 0.5, y: 1.4, w: 12.3, fontSize: 12, border: { type: "solid", pt: 0.5, color: BORDER }, colW: [3.5, 5.3, 1.8, 1.7] });
  drawFooter(ms, "15 · Milestones", W, H);

  // ── Slide 16 · Budget ─────────────────────────────────────────────────
  const bg = ppt.addSlide();
  bg.background = { color: PAPER };
  drawHeader(bg, ppt, "Budget", "Portfolio · Allocated · Spent · Forecast", W);
  const bgRows = [
    [head("Project"), head("Allocated"), head("Spent"), head("Forecast"), head("Variance")],
    ...proj.slice(0, 10).map(p => {
      const alloc = p.budget || 0; const spent = p.spent || 0; const fcst = p.forecast || alloc; const varc = fcst - alloc;
      return [
        cell(p.name),
        { text: money(alloc), options: { align: "right", color: INK } },
        { text: money(spent), options: { align: "right", color: INK } },
        { text: money(fcst),  options: { align: "right", color: INK } },
        { text: money(varc),  options: { align: "right", bold: true, color: varc > 0 ? "DC2626" : "16A34A" } },
      ];
    }),
  ];
  bg.addTable(bgRows, { x: 0.5, y: 1.4, w: 12.3, fontSize: 11, border: { type: "solid", pt: 0.5, color: BORDER }, colW: [3.5, 2.2, 2.2, 2.2, 2.2] });
  drawFooter(bg, "16 · Budget", W, H);

  // ── Slide 17 · Open Risks ─────────────────────────────────────────────
  const or = ppt.addSlide();
  or.background = { color: PAPER };
  drawHeader(or, ppt, "Open Risks", "By project", W);
  const openRisksRows = [
    [head("Project"), head("Ref"), head("Title"), head("Score"), head("Status")],
    ...(rollup?.aggregates?.criticalRisks || []).slice(0, 10).map(r => [
      cell(r.project || "—"), cell(r.ref || "—"), cell(r.title), { text: r.score || "—", options: { bold: true, color: r.score === "Critical" ? "DC2626" : "D97706", align: "center" } }, cell(r.status || "Open"),
    ]),
  ];
  or.addTable(openRisksRows, { x: 0.5, y: 1.4, w: 12.3, fontSize: 11, border: { type: "solid", pt: 0.5, color: BORDER }, colW: [2.8, 1.4, 5.7, 1.2, 1.2] });
  drawFooter(or, "17 · Open Risks", W, H);

  // ── Slide 17b · Schedule items currently at risk ──────────────────────
  // Live-sourced from POST /api/portfolio/schedule-at-risk which aggregates
  // per-project schedule_items_at_risk() output. Ranks soonest first.
  const sar = ppt.addSlide();
  sar.background = { color: PAPER };
  drawHeader(sar, ppt, "Schedule items currently at risk", "Ranked by soonest date · linked from live RAID log", W);
  const sarItems = (schAtRisk?.items) || [];
  const sarRows = [
    [head("Project"), head("Milestone / Task"), head("Date"), head("Days"), head("Risks"), head("Max severity")],
    ...(sarItems.length ? sarItems.slice(0, 12) : [{ project: "—", name: "No linked schedule items at risk", date: "—", days_away: null, linked_risk_count: 0, max_severity: "—" }]).map(x => {
      const daysCell = x.days_away == null
        ? { text: "—", options: { align: "right", color: INK_SOFT } }
        : x.days_away < 0
          ? { text: `${-x.days_away}d late`, options: { align: "right", bold: true, color: "DC2626" } }
          : { text: `${x.days_away}d`, options: { align: "right", color: x.days_away <= 14 ? "D97706" : INK } };
      const sevColor = x.max_severity === "Critical" ? "DC2626" : x.max_severity === "High" ? "F97316" : x.max_severity === "Medium" ? "D97706" : INK_SOFT;
      return [
        cell(x.project || "—"),
        cell(x.name || "—"),
        cell(x.date || "—"),
        daysCell,
        { text: String(x.linked_risk_count || 0), options: { align: "center", bold: true, color: (x.linked_risk_count || 0) > 1 ? "DC2626" : INK } },
        { text: x.max_severity || "—", options: { align: "center", bold: true, color: sevColor } },
      ];
    }),
  ];
  sar.addTable(sarRows, { x: 0.5, y: 1.4, w: 12.3, fontSize: 11, border: { type: "solid", pt: 0.5, color: BORDER }, colW: [2.6, 4.8, 1.6, 1.4, 0.9, 1.0] });
  sar.addText("Auto-generated from Risk-to-Schedule Linkage — every row above is one or more open RAID risks linked to that milestone / task. Follow the count column into the RAID log to see the underlying risks.",
    { x: 0.5, y: 6.6, w: 12.3, h: 0.6, fontSize: 9, italic: true, color: INK_SOFT });
  drawFooter(sar, "17b · Schedule items at risk", W, H);

  // ── Slide 18 · Open Actions ───────────────────────────────────────────
  const oa = ppt.addSlide();
  oa.background = { color: PAPER };
  drawHeader(oa, ppt, "Open Actions", "Next 14 days", W);
  const overdue = rollup?.aggregates?.actionsOverdue || [];
  const oaRows = [
    [head("Project"), head("Action"), head("Owner"), head("Due"), head("Severity")],
    ...(overdue.length ? overdue.slice(0, 10) : [{ project: "—", title: "No overdue actions", assignee: "—", due: "—", severity: "—" }]).map(a => [
      cell(a.project || "—"), cell(a.title), cell(a.assignee || "—"), cell(a.due || "—"),
      { text: a.severity || "Normal", options: { color: a.severity === "High" ? "DC2626" : a.severity === "Medium" ? "D97706" : INK_SOFT, bold: true } },
    ]),
  ];
  oa.addTable(oaRows, { x: 0.5, y: 1.4, w: 12.3, fontSize: 11, border: { type: "solid", pt: 0.5, color: BORDER }, colW: [2.5, 5, 2, 1.5, 1.3] });
  drawFooter(oa, "18 · Open Actions", W, H);

  // ── Slide 19 · Decisions Requested ────────────────────────────────────
  const dq = ppt.addSlide();
  dq.background = { color: PAPER };
  drawHeader(dq, ppt, "Decisions Requested", "Board approvals needed this session", W);
  const decisions = [
    { d: `Approve S/4HANA Wave 3 (HCM) cutover window: ${hana?.migration.waves.find(w => w.wave.includes("HCM"))?.date || "May 15"}` },
    { d: `Ratify additional $${value ? Math.round(value.totals.atRisk / 1_000_000) : 8}M contingency for at-risk value initiatives` },
    { d: `Approve Dynamics 365 tenant migration to Azure Sovereign Cloud (EU)` },
    { d: `Endorse security posture uplift roadmap — quarterly board updates` },
    { d: `Accept 1-quarter deferral on cybersecurity value realisation` },
  ];
  decisions.forEach((x, i) => {
    const y = 1.5 + i * 0.9;
    dq.addShape(ppt.ShapeType.roundRect, { x: 0.6, y, w: 0.8, h: 0.6, fill: { color: CHERRY }, line: { color: CHERRY }, rectRadius: 0.08 });
    dq.addText(String(i + 1), { x: 0.6, y, w: 0.8, h: 0.6, fontSize: 20, bold: true, color: "FFFFFF", align: "center", valign: "middle" });
    dq.addText(x.d, { x: 1.6, y: y + 0.05, w: 11, h: 0.6, fontSize: 13, color: INK, valign: "middle" });
  });
  drawFooter(dq, "19 · Decisions Requested", W, H);

  // ── Slide 20 · Next Quarter Focus ─────────────────────────────────────
  const nq = ppt.addSlide();
  nq.background = { color: CHERRY };
  nq.addText("NEXT QUARTER FOCUS", { x: 0.6, y: 0.6, w: 12, h: 0.4, fontSize: 12, bold: true, color: "FFFFFF", charSpacing: 4 });
  nq.addText("Where the executive team will invest attention", { x: 0.6, y: 1, w: 12, h: 0.4, fontSize: 14, color: "E5A9B3" });
  const focus = [
    { h: "01 · S/4HANA Wave 3 delivery", b: "HCM cutover · adoption push · training completion" },
    { h: "02 · D365 CRM adoption",       b: "Close usage gap · CRM playbooks · training refresh" },
    { h: "03 · Value realisation",       b: "Recover Cybersecurity benefits · protect Cloud consolidation" },
    { h: "04 · APAC hires + pipeline",    b: "Convert 3.8× coverage into bookings · accelerate hiring" },
  ];
  focus.forEach((f, i) => {
    const y = 2.0 + i * 1.15;
    nq.addText(f.h, { x: 0.6, y, w: 12, h: 0.4, fontSize: 18, bold: true, color: "FFFFFF" });
    nq.addText(f.b, { x: 0.6, y: y + 0.5, w: 12, h: 0.5, fontSize: 13, color: "E5A9B3" });
  });
  nq.addText("HARMONIZER · CAPITTANCE.COM", { x: 0.6, y: H - 0.5, w: 12, h: 0.3, fontSize: 9, color: "E5A9B3", charSpacing: 3, align: "right" });

  // If the user has an active $50 template unlock + PPTX uploaded, merge the
  // template's theme (colours + fonts + master-slide media) into the deck
  // before download. Falls back to the unmodified pack on any failure.
  const filename = `board-pack-${todayStr()}.pptx`;
  if (useTemplate) {
    const blob = await ppt.write({ outputType: "blob" });
    const merged = await applyCustomPptxTemplate(blob);
    const url = URL.createObjectURL(merged);
    const a = document.createElement("a");
    a.href = url; a.download = filename; document.body.appendChild(a); a.click();
    document.body.removeChild(a); URL.revokeObjectURL(url);
    return { slides: 20, template_applied: true };
  }
  await ppt.writeFile({ fileName: filename });
  return { slides: 20 };
}

// ─── helpers ─────────────────────────────────────────────────────────────
function head(t)   { return { text: String(t), options: { bold: true, color: "FFFFFF", fill: { color: CHERRY }, align: "left" } }; }
function cell(t)   { return { text: String(t), options: { color: INK } }; }
function sumBy(arr, k) { return (arr || []).reduce((s, x) => s + (x[k] || 0), 0); }
function countBy(arr, k, v) { return (arr || []).filter(x => x[k] === v).length; }

function drawHeader(slide, ppt, title, sub, W) {
  slide.addShape(ppt.ShapeType.rect, { x: 0, y: 0, w: W, h: 0.9, fill: { color: CHERRY }, line: { color: CHERRY } });
  slide.addText(title, { x: 0.5, y: 0.15, w: 10, h: 0.4, fontSize: 22, bold: true, color: "FFFFFF" });
  if (sub) slide.addText(sub, { x: 0.5, y: 0.55, w: 10, h: 0.3, fontSize: 11, color: "E5A9B3" });
}
function drawFooter(slide, tag, W, H) {
  slide.addText(tag,                             { x: 0.5, y: H - 0.35, w: 6,        h: 0.3, fontSize: 9, color: INK_SOFT, charSpacing: 2 });
  slide.addText("HARMONIZER · capittance.com",   { x: W - 4, y: H - 0.35, w: 3.5,    h: 0.3, fontSize: 9, color: INK_SOFT, align: "right", charSpacing: 2 });
}

function drawKpiGrid(slide, ppt, tiles, opts = {}) {
  const compact = !!opts.compact;
  const startY = 1.4;
  const tileH = compact ? 1.4 : 1.6;
  const gap = 0.2;
  const cols = 4;
  const tileW = (13.333 - 1.2 - gap * (cols - 1)) / cols;
  tiles.forEach((t, i) => {
    const col = i % cols;
    const x = 0.6 + col * (tileW + gap);
    const y = startY;
    slide.addShape(ppt.ShapeType.roundRect, { x, y, w: tileW, h: tileH, fill: { color: "FFFFFF" }, line: { color: BORDER, width: 0.5 }, rectRadius: 0.08 });
    slide.addShape(ppt.ShapeType.roundRect, { x, y, w: 0.08, h: tileH, fill: { color: t.color }, line: { color: t.color }, rectRadius: 0.03 });
    slide.addText(t.label, { x: x + 0.25, y: y + 0.15, w: tileW - 0.35, h: 0.3, fontSize: 9,  bold: true, color: INK_SOFT, charSpacing: 2 });
    slide.addText(t.value, { x: x + 0.25, y: y + 0.45, w: tileW - 0.35, h: 0.75, fontSize: 26, bold: true, color: t.color });
    slide.addText(t.sub || "", { x: x + 0.25, y: y + tileH - 0.4, w: tileW - 0.35, h: 0.3, fontSize: 10, color: INK });
  });
}
